import { useRoomSummaryQuery, useSessionSummaryQuery } from "@crowdcoursing/api/graphql";
import {
  useBubbleGetBubble,
  useBubbleGetBubbleCheckInQrCode,
  useEventGetEventCheckInQrCode,
  useRoomGetRoomCheckInQrCode,
} from "@crowdcoursing/api/openapi";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { valueOrFallback } from "~utils/sanitizeDto";

import { QRCodeTemplate } from "../../templates/CheckIn/QRCodeTemplate";

const tLoading = "Loading QR code...";
const tErrorUnavailable = "QR code not available";
const tErrorLoading = "Error loading data";
const tAdminOnlyNote = "Note: Only administrators can access this QR code.";
const tDefaultTitle = "Join";
const tScanQRCode = "Scan the code above to join and check in";
const tInstructions = [
  "• If not logged in, they will be directed to log in first",
  "• After logging in, they will be automatically checked in",
  "• They will receive a confirmation notification",
];

type QRCodeType = "bubble" | "room" | "event";

export const QRCodePage = () => {
  const { bubbleId, roomId, eventId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [svgData, setSvgData] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState<string>(tDefaultTitle);

  // Determine the type of QR code being requested
  const type: QRCodeType = bubbleId ? "bubble" : roomId ? "room" : "event";

  // Fetch bubble data to display the name
  const {
    data: bubbleData,
    error: bubbleError,
    isLoading: isBubbleLoading,
  } = useBubbleGetBubble(
    {
      pathParams: {
        bubbleId: valueOrFallback(bubbleId, "MISSING_BUBBLE_ID", "(QRCodePage) Missing bubbleId from url params"),
      },
    },
    { enabled: type === "bubble" },
  );

  // Fetch room data to display the name
  const {
    data: roomData,
    error: roomError,
    isLoading: isRoomLoading,
  } = useRoomSummaryQuery(
    {
      id: valueOrFallback(roomId, "MISSING_ROOM_ID", "(QRCodePage) Missing roomId from url params"),
    },
    {
      enabled: type === "room",
      select: (data) => data.room,
    },
  );

  // Fetch event data to display the name
  const {
    data: eventData,
    error: eventError,
    isLoading: isEventLoading,
  } = useSessionSummaryQuery(
    {
      eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(QRCodePage) Missing eventId from url params"),
    },
    {
      enabled: type === "event",
      select: (data) => data.session,
    },
  );

  // Get the QR code based on type
  const {
    data: bubbleQrCodeData,
    error: bubbleQrCodeError,
    isLoading: isBubbleQrCodeLoading,
  } = useBubbleGetBubbleCheckInQrCode(
    { pathParams: { bubbleId: valueOrFallback(bubbleId, "MISSING_BUBBLE_ID", "(QRCodePage) Missing bubbleId") } },
    { enabled: type === "bubble", staleTime: 5 * 60 * 1000 },
  );

  const {
    data: roomQrCodeData,
    error: roomQrCodeError,
    isLoading: isRoomQrCodeLoading,
  } = useRoomGetRoomCheckInQrCode(
    { pathParams: { roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "(QRCodePage) Missing roomId") } },
    { enabled: type === "room", staleTime: 5 * 60 * 1000 },
  );

  const {
    data: eventQrCodeData,
    error: eventQrCodeError,
    isLoading: isEventQrCodeLoading,
  } = useEventGetEventCheckInQrCode(
    { pathParams: { eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(QRCodePage) Missing eventId") } },
    { enabled: type === "event", staleTime: 5 * 60 * 1000 },
  );

  // Process QR code data when it's available
  useEffect(() => {
    const qrCodeData = type === "bubble" ? bubbleQrCodeData : type === "room" ? roomQrCodeData : eventQrCodeData;
    if (qrCodeData) {
      const svg = qrCodeData as string;
      const blob = new Blob([svg], { type: "image/svg+xml" });
      setSvgData(URL.createObjectURL(blob));
    }
  }, [bubbleQrCodeData, roomQrCodeData, eventQrCodeData, type]);

  // Handle errors and set title
  useEffect(() => {
    if (type === "bubble") {
      if (bubbleError) {
        setError(tErrorLoading);
      } else if (bubbleQrCodeError) {
        setError(tErrorUnavailable);
      } else if (!svgData && !isBubbleQrCodeLoading) {
        setError(tErrorUnavailable);
      } else if (svgData) {
        setError(null);
      }
      setTitle(bubbleData?.name || tDefaultTitle);
    } else if (type === "room") {
      if (roomError) {
        setError(tErrorLoading);
      } else if (roomQrCodeError) {
        setError(tErrorUnavailable);
      } else if (!svgData && !isRoomQrCodeLoading) {
        setError(tErrorUnavailable);
      } else if (svgData) {
        setError(null);
      }
      setTitle(roomData?.name || tDefaultTitle);
    } else if (type === "event") {
      if (eventError) {
        setError(tErrorLoading);
      } else if (eventQrCodeError) {
        setError(tErrorUnavailable);
      } else if (!svgData && !isEventQrCodeLoading) {
        setError(tErrorUnavailable);
      } else if (svgData) {
        setError(null);
      }
      setTitle(eventData?.name || tDefaultTitle);
    }
  }, [
    type,
    bubbleData,
    roomData,
    eventData,
    bubbleError,
    roomError,
    eventError,
    bubbleQrCodeError,
    roomQrCodeError,
    eventQrCodeError,
    svgData,
    isBubbleQrCodeLoading,
    isRoomQrCodeLoading,
    isEventQrCodeLoading,
  ]);

  const isLoading =
    (type === "bubble" && (isBubbleLoading || isBubbleQrCodeLoading)) ||
    (type === "room" && (isRoomLoading || isRoomQrCodeLoading)) ||
    (type === "event" && (isEventLoading || isEventQrCodeLoading));

  if (isLoading) {
    return (
      <QRCodeTemplate
        adminNote={tAdminOnlyNote}
        error={tLoading}
        instructions={tInstructions}
        qrCodeSrc=""
        subtitle={tScanQRCode}
        title={title}
      />
    );
  }

  return (
    <QRCodeTemplate
      adminNote={tAdminOnlyNote}
      error={error}
      instructions={tInstructions}
      qrCodeSrc={svgData || ""}
      subtitle={tScanQRCode}
      title={title}
    />
  );
};
